import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const FREE_LABEL = { es: '¡Gratis!', de: 'Kostenlos!', en: 'Free!' };

const PRICING_LINK = {
  es: { path: '/clases-particulares-espanol-online', label: 'Cómo son las clases particulares de español online' },
  de: { path: '/de/spanisch-einzelunterricht-online', label: 'So funktioniert der Spanisch Einzelunterricht online' },
  en: { path: '/en/private-spanish-lessons-online', label: 'How private online Spanish lessons work' },
};
import { Check, Star, ArrowRight } from 'lucide-react';
import { CLASS_PLANS } from '../config/classPlans';
import { trackTrialCtaClick } from '../lib/analytics';

const CONTENT = {
  es: {
    tag: 'TARIFAS',
    title: 'Precios claros, sin compromiso',
    subtitle: 'La primera clase de prueba es gratuita. Después, si quieres continuar, puedes elegir la opción que mejor se adapte a ti.',
    popular: 'MÁS POPULAR',
    cta: 'Reservar ahora',
    ctaFree: 'Reservar clase gratuita',
    saveBadge: (amount) => `Ahorras ${amount}`,
    perClass: (amount) => `${amount}/clase`,
    note: 'Todas las clases son 1-a-1, online por Zoom o Google Meet. Los packs no caducan.',
    deleNote: 'Preparación para todos los niveles DELE (A1–C2) con Cecilia, profesora de español y examinadora acreditada DELE B1 por el Instituto Cervantes.',
    plans: CLASS_PLANS.es,
  },
  en: {
    tag: 'PRICING',
    title: 'Clear prices, no surprises',
    subtitle: 'Choose the plan that best fits your pace and goals. No commitment, you can switch anytime.',
    popular: 'MOST POPULAR',
    cta: 'Book now',
    ctaFree: 'Book free class',
    saveBadge: (amount) => `Save ${amount}`,
    perClass: (amount) => `${amount}/class`,
    note: 'All classes are 1-on-1, online via Zoom or Google Meet. Packs do not expire.',
    deleNote: 'DELE preparation for all levels (A1–C2) with Cecilia, Spanish teacher and accredited DELE B1 examiner (Instituto Cervantes).',
    plans: CLASS_PLANS.en,
  },
  de: {
    tag: 'TARIFE',
    title: 'Klare Preise, keine Überraschungen',
    subtitle: 'Wähle den Plan, der zu deinem Tempo und deinen Zielen passt. Ohne Bindung, jederzeit wechselbar.',
    popular: 'AM BELIEBTESTEN',
    cta: 'Jetzt buchen',
    ctaFree: 'Kostenlose Stunde buchen',
    saveBadge: (amount) => `Spare ${amount}`,
    perClass: (amount) => `${amount}/Stunde`,
    note: 'Alle Kurse sind 1-zu-1, online über Zoom oder Google Meet. Pakete verfallen nicht.',
    deleNote: 'DELE-Vorbereitung für alle Niveaus (A1–C2) mit Cecilia, Spanischlehrerin und vom Instituto Cervantes akkreditierte DELE-B1-Prüferin.',
    plans: CLASS_PLANS.de,
  },
};

export const Pricing = () => {
  const { language } = useLanguage();
  const lang = CONTENT[language] ? language : 'es';
  const c = CONTENT[lang];
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const scrollToBooking = (planId) => {
    if (planId === 'prueba') trackTrialCtaClick('pricing');
    // Preselecciona el plan en la sección de reservas antes de hacer scroll
    window.dispatchEvent(new CustomEvent('select-class-plan', { detail: planId }));
    const el = document.getElementById('booking');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="pricing"
      ref={ref}
      className="relative py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-[#FFF7F6] to-white overflow-hidden"
    >
      {/* Decorative circles */}
      <div aria-hidden className="absolute top-20 -left-20 w-72 h-72 bg-[#A8D8EA]/15 rounded-full blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute bottom-20 -right-20 w-72 h-72 bg-[#F5A6A4]/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <span className="text-[#F5A6A4] font-semibold text-sm uppercase tracking-wider" data-testid="pricing-tag">
            {c.tag}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#4A4A4A] mt-4 mb-4">{c.title}</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mx-auto mb-6" />
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">{c.subtitle}</p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pricing-cards">
          {c.plans.map((plan, idx) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 + idx * 0.08 }}
              whileHover={{ y: -6 }}
              data-testid={`pricing-card-${plan.id}`}
              className={`relative bg-white rounded-3xl p-6 flex flex-col shadow-sm hover:shadow-xl transition-shadow border-2 ${
                plan.featured
                  ? 'border-[#F5A6A4] shadow-lg lg:-my-4 lg:py-10'
                  : 'border-gray-100'
              }`}
            >
              {/* Featured badge */}
              {plan.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#F5A6A4] to-[#E38987] text-white text-xs font-bold rounded-full shadow-md whitespace-nowrap">
                  <Star className="w-3 h-3 fill-white" />
                  {c.popular}
                </div>
              )}

              {/* Free badge */}
              {plan.free && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white text-xs font-bold rounded-full shadow-md whitespace-nowrap">
                  {FREE_LABEL[language] || FREE_LABEL.es}
                </div>
              )}

              {/* Save badge */}
              {plan.save && !plan.featured && (
                <div className="absolute top-4 right-4 px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold rounded-md border border-green-200">
                  -{plan.save}
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-lg font-bold text-[#4A4A4A] mb-1">{plan.name}</h3>
                <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">{plan.duration}</p>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline gap-2">
                  <span className={`text-4xl font-extrabold ${plan.free ? 'text-[#7DBDD9]' : 'text-[#4A4A4A]'}`}>
                    {plan.price}
                  </span>
                </div>
                {plan.perClassPrice && (
                  <p className="text-xs text-gray-500 mt-1">{c.perClass(plan.perClassPrice)}</p>
                )}
              </div>

              <p className="text-sm text-gray-600 mb-5 min-h-[40px]">{plan.description}</p>

              <ul className="space-y-2.5 mb-6 flex-1">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className="w-4 h-4 text-[#7DBDD9] flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => scrollToBooking(plan.id)}
                data-testid={`pricing-cta-${plan.id}`}
                className={`w-full py-2.5 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-1.5 group ${
                  plan.featured
                    ? 'bg-gradient-to-r from-[#F5A6A4] to-[#E38987] text-white hover:shadow-lg'
                    : plan.free
                    ? 'bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white hover:shadow-lg'
                    : 'bg-white text-[#4A4A4A] border border-gray-200 hover:border-[#7DBDD9] hover:text-[#7DBDD9]'
                }`}
              >
                <span>{plan.free ? c.ctaFree : c.cta}</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.7, delay: 0.8 }}
          className="text-center text-sm text-gray-500 mt-10 max-w-2xl mx-auto italic"
        >
          {c.note}
        </motion.p>

        {c.deleNote && (
          <p className="text-center text-sm text-gray-500 mt-3 max-w-2xl mx-auto italic" data-testid="pricing-dele-note">
            {c.deleNote}
          </p>
        )}

        <p className="text-center mt-6">
          <Link
            to={PRICING_LINK[language].path}
            data-testid="pricing-private-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#5DA6C9] hover:text-[#4A4A4A] transition-colors"
          >
            {PRICING_LINK[language].label}
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </p>
      </div>
    </section>
  );
};
